import React, { useState } from 'react';
import { leaveSlotService, LeaveSlot } from '../../../services/availability/leaveSlotService';
import { DoctorPractice } from '../../../services/profile/profileService';
import EditLeaveForm from '../EditLeaveForm/EditLeaveForm';
import './LeaveDetailsView.css';

interface LeaveDetailsViewProps {
  leave: LeaveSlot;
  practices: DoctorPractice[];
  onClose: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}

const LeaveDetailsView: React.FC<LeaveDetailsViewProps> = ({
  leave,
  practices,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const [showingEditView, setShowingEditView] = useState<boolean>(false);
  const [showingCancelAlert, setShowingCancelAlert] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDuration = (start: string, end: string): string => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const durationHours = (endMinutes - startMinutes) / 60;

    if (durationHours === 0) {
      return '0 hours';
    } else if (durationHours === 1) {
      return '1 hour';
    } else if (durationHours % 1 === 0) {
      return `${Math.floor(durationHours)} hours`;
    } else {
      return `${durationHours.toFixed(1)} hours`;
    }
  };

  const formatLeaveType = (type: string): string => {
    const typeMap: Record<string, string> = {
      holiday: 'Holiday',
      sick_leave: 'Sick Leave',
      emergency: 'Emergency',
      other: 'Other',
    };
    return typeMap[type] || type;
  };

  const getLeaveTypeColor = (type: string): string => {
    const colorMap: Record<string, string> = {
      holiday: '#3b82f6',
      sick_leave: '#ef4444',
      emergency: '#f59e0b',
      other: '#6b7280',
    };
    return colorMap[type] || '#6b7280';
  };

  const getPracticeName = (): string => {
    if (leave.leave_scope === 'all_practices') {
      return 'All Practices';
    }
    if (leave.practice_id) {
      const practice = practices.find((p) => p.id === leave.practice_id);
      return practice?.practice_name || 'Unknown Practice';
    }
    return 'Unknown Practice';
  };

  const handleCancelLeave = async () => {
    setIsCancelling(true);
    try {
      await leaveSlotService.cancelLeaveSlot(leave.id, true);
      onDelete();
      onClose();
    } catch (err: any) {
      console.error('Error cancelling leave:', err);
      alert(err.response?.data?.detail || err.message || 'Failed to cancel leave');
      setIsCancelling(false);
    }
  };

  if (showingEditView) {
    return (
      <EditLeaveForm
        leave={leave}
        practices={practices}
        onClose={() => setShowingEditView(false)}
        onSuccess={() => {
          setShowingEditView(false);
          onUpdate();
        }}
      />
    );
  }

  return (
    <div className="leave-details-view">
      <div className="leave-details-header">
        <h2>Leave Details</h2>
        <button type="button" className="leave-details-close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="leave-details-body">
        <div className="leave-details-section">
          <h3 className="leave-details-section-title">Leave Information</h3>
          <div className="leave-details-row">
            <span className="leave-details-label">Date</span>
            <span className="leave-details-value">{formatDate(leave.leave_date)}</span>
          </div>
          <div className="leave-details-row">
            <span className="leave-details-label">Time</span>
            <div className="leave-details-time-group">
              <span className="leave-details-value">
                {formatTime(leave.start_time)} - {formatTime(leave.end_time)}
              </span>
              <span className="leave-details-duration">
                ({formatDuration(leave.start_time, leave.end_time)})
              </span>
            </div>
          </div>
          <div className="leave-details-row">
            <span className="leave-details-label">Type</span>
            <span
              className="leave-details-value"
              style={{ color: getLeaveTypeColor(leave.leave_type) }}
            >
              {formatLeaveType(leave.leave_type)}
            </span>
          </div>
          <div className="leave-details-row">
            <span className="leave-details-label">Practice</span>
            <span className="leave-details-value">{getPracticeName()}</span>
          </div>
          <div className="leave-details-row">
            <span className="leave-details-label">Status</span>
            <span className={`leave-details-status leave-details-status-${leave.status}`}>
              {leave.status}
            </span>
          </div>
          {leave.reason && (
            <div className="leave-details-row leave-details-row-vertical">
              <span className="leave-details-label">Reason</span>
              <span className="leave-details-value">{leave.reason}</span>
            </div>
          )}
        </div>

        <div className="leave-details-actions">
          <button
            type="button"
            className="leave-details-edit-button"
            onClick={() => setShowingEditView(true)}
          >
            <span>✏️</span>
            <span>Edit Leave</span>
          </button>
          <button
            type="button"
            className="leave-details-cancel-button"
            onClick={() => setShowingCancelAlert(true)}
            disabled={isCancelling}
          >
            <span>🗑️</span>
            <span>{isCancelling ? 'Cancelling...' : 'Cancel Leave'}</span>
          </button>
        </div>
      </div>

      {showingCancelAlert && (
        <div className="modal-overlay" onClick={() => setShowingCancelAlert(false)}>
          <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Leave</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this leave? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-button-secondary"
                onClick={() => setShowingCancelAlert(false)}
              >
                No, Keep Leave
              </button>
              <button
                type="button"
                className="modal-button-danger"
                onClick={handleCancelLeave}
              >
                Yes, Cancel Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveDetailsView;

